import React, { useEffect } from 'react';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import { isFedRamp } from '@atlassian/atl-context';
import { ff } from '@atlassian/jira-feature-flagging';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { JsmRepackagingBanner } from '@atlassian/jira-jsm-repackaging-banner/src/async.tsx';
import { useAnalyticsEvents, FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import PageSpinner from '@atlassian/jira-servicedesk-common/src/ui/components/page-spinner';
import { useGetCustomFields } from '@atlassian/jira-servicedesk-custom-fields/src/services/use-get-custom-fields/index.tsx';
import { INCIDENTS } from '@atlassian/jira-servicedesk-work-category/src/common/constants.tsx';
import { toProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { sectionParagraphStyles } from '../common/ui';
import {
	CommunicationsConnectionsContainer,
	useShouldSendResponderAlerts,
	PIRSettingsContainer,
} from '../services/main.tsx';
import { CommunicationBridgesSection } from './communication-bridges';
import { IncidentManagementFieldsSection } from './incident-management-fields';
import messages from './messages';
import { PostIncidentReviewSection } from './post-incident-reviews';
import { ToggleResponderAlertsSection } from './responder-alerts';

export const IncidentManagementSettings = () => {
	const { formatMessage } = useIntl();
	const { cloudId } = useTenantContext();
	const { data: projectContext } = useProjectContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [{ responderAlertsConfigurationData, error }, { fetchResponderAlertsConfiguration }] =
		useShouldSendResponderAlerts();

	const [
		{ data: customFields, loading: customFieldsLoading, error: customFieldsError },
		{ fetchCustomFields },
	] = useGetCustomFields();

	const { showFlag } = useFlagsService();

	const projectId = projectContext ? toProjectId(projectContext.projectId.toString()) : undefined;

	useEffect(() => {
		if (!customFieldsError && !customFieldsLoading && !customFields) {
			fetchCustomFields();
		}
	}, [customFieldsLoading, customFields, fetchCustomFields, customFieldsError]);

	useEffect(() => {
		fetchResponderAlertsConfiguration({
			cloudId,
			projectId,
			analyticsEvent: createAnalyticsEvent({}),
		});
	}, [cloudId, projectId, createAnalyticsEvent, fetchResponderAlertsConfiguration]);

	useEffect(() => {
		if (error) {
			showFlag({
				type: 'error',
				title: messages.errorTitle,
				description: messages.errorDescription,
			});
		}
	}, [error, showFlag]);

	if (responderAlertsConfigurationData === undefined) {
		return <PageSpinner />;
	}

	return (
		<>
			<Heading size="large">{formatMessage(messages.pageTitle)}</Heading>

			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
			<Box xcss={[sectionParagraphStyles]}>{formatMessage(messages.description)}</Box>
			{ff('enable_jsm_repackaging_changes_vtwk9') && (
				<Box xcss={jsmRepackagingBannerStyles}>
					<JsmRepackagingBanner workCategory={INCIDENTS} />
				</Box>
			)}
			<FireScreenAnalytics />
			<IncidentManagementFieldsSection />
			<ToggleResponderAlertsSection />
			{!isFedRamp() && (
				<CommunicationsConnectionsContainer
					projectId={projectId}
					cloudId={cloudId}
					analyticsEvent={createAnalyticsEvent({})}
				>
					<CommunicationBridgesSection />
				</CommunicationsConnectionsContainer>
			)}
			<PIRSettingsContainer
				cloudId={cloudId}
				analyticsEvent={createAnalyticsEvent({})}
				projectId={projectId}
			>
				<PostIncidentReviewSection />
			</PIRSettingsContainer>
		</>
	);
};

const jsmRepackagingBannerStyles = xcss({
	marginBottom: 'space.500',
});
